import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

export function DonutChart({ title, data, colors, size: forcedSize }) {
  const ref = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = '';

    const measured = Math.min(el.clientWidth || 220, 240);
    const size = forcedSize || measured;
    const width = size;
    const height = size;
    const radius = Math.min(width, height) / 2;
    const innerR = radius * 0.6;

    const svg = d3
      .select(el)
      .append('svg')
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    const valueSum = d3.sum(data, d => d.value) || 1;

    const pie = d3
      .pie()
      .sort(null)
      .value(d => d.value);

    const arc = d3
      .arc()
      .innerRadius(innerR)
      .outerRadius(radius);

    const color = d3.scaleOrdinal().domain(data.map(d => d.label)).range(colors);

    svg
      .selectAll('path')
      .data(pie(data))
      .enter()
      .append('path')
      .attr('fill', d => color(d.data.label))
      .attr('d', arc)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', d => (d.data.muted ? 0.25 : 1));

    const tooltip = d3.select(tooltipRef.current);
    const containerEl = el.parentElement;

    // tooltip에 pointer-events: none 추가하여 마우스 이벤트 차단 방지
    tooltip.style('pointer-events', 'none');

    svg
      .selectAll('path')
      .on('mouseenter', function (event, d) {
        tooltip.style('display', 'block');
      })
      .on('mousemove', function (event, d) {
        const rawPercent = d.data.percent != null && d.data.percent !== '' ? Number(d.data.percent) : null;
        const percent = rawPercent != null && !isNaN(rawPercent)
          ? rawPercent
          : Math.round((d.data.value / valueSum) * 100);
        const percentText = typeof percent === 'number' && percent % 1 !== 0
          ? percent.toFixed(2).replace(/\.?0+$/, '')
          : String(percent);

        // 브라우저 좌표계로 마우스 위치 가져오기
        const [mx, my] = d3.pointer(event, el);
        const containerRect = containerEl.getBoundingClientRect();

        const tooltipWidth = 160;
        const tooltipHeight = 70;
        const offset = 15; // 마우스와 tooltip 사이 간격 증가

        // 컨테이너 내부에서의 상대 좌표로 변환
        let left = mx + offset;
        let top = my - tooltipHeight / 2;

        // 화면 밖으로 나가지 않도록 조정
        if (left + tooltipWidth > containerRect.width) {
          left = mx - tooltipWidth - offset;
        }
        if (left < 0) {
          left = offset;
        }
        if (top < 0) {
          top = offset;
        }
        if (top + tooltipHeight > containerRect.height) {
          top = containerRect.height - tooltipHeight - offset;
        }

        tooltip
          .style('left', `${left}px`)
          .style('top', `${top}px`)
          .html(`
            <div class='px-3 py-2'>
              <div class='flex items-center gap-2 mb-1'>
                <span class='inline-block w-[8px] h-[8px] rounded-full shrink-0' style='background:${color(d.data.label)}'></span>
                <span class='font-bold text-[#2A2E36] leading-none'>${d.data.label}</span>
              </div>
              <div class='flex items-center justify-between gap-2'>
                <span class='text-[#8D9AAF] text-[12px]'>Value:</span>
                <span class='text-[#2A2E36] font-semibold'>${d.data.value}</span>
              </div>
              <div class='flex items-center justify-between gap-2'>
                <span class='text-[#8D9AAF] text-[12px]'>Percentage:</span>
                <span class='text-[#2A2E36] font-semibold'>${percentText}%</span>
              </div>
            </div>`);
      })
      .on('mouseleave', () => {
        tooltip.style('display', 'none');
      });

    // center title
    svg
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('class', 'fill-[#2A2E36]')
      .style('font-weight', 800)
      .style('font-size', 20)
      .text(title);
  }, [title, data, colors, forcedSize]);

  return (
    <div className="relative">
      <div ref={ref} className="w-full" />
      <div
        ref={tooltipRef}
        className="absolute z-10 hidden bg-white border border-[#E4E8F1] rounded-xl shadow-sm text-[13px]"
        style={{ display: 'none', pointerEvents: 'none' }}
      />
    </div>
  );
}

export default DonutChart;
